import type { AgentRuntimeSnapshot, AgentType, StoredSessionIntent } from "@cord/shared"
import type { ChatGptOauthDebug } from "@/lib/chatgpt-oauth-debug"
import type {
  ThreadSyncRenderSource,
  ThreadSyncResumeSource,
} from "@/lib/thread-sync-activation-contract"
import type { PrAdoptionControlPlaneSnapshot } from "@/lib/thread-sync-control-plane"
import type { ThreadRuntimeMaterialization } from "@/lib/thread-sync-runtime-materialization"
import {
  buildQueuedMessages,
  type LocalOutboxQueueRecord,
  type PendingQueueRecord,
  type QueuedMessage,
  type SandboxStartupQueueKind,
  type SandboxStatus,
} from "@/lib/session-queue-ui-state"
import type {
  ActiveCredentialSourceSnapshot,
  ActiveEditMessageOperation,
  ClientWsCloseDiagnostics,
  ClientWsErrorDiagnostics,
  CodeServerStatus,
  CredentialIssueSnapshot,
  DevServerStatus,
  MessageRejectionEvent,
  QueuedSessionCommandOutcomeNotice,
  PreviewEndpoint,
  RemoteWsDiagnostics,
  RuntimeProcessSnapshot,
  SandboxProgress,
  SessionActionOutcomeNotice,
  SessionAgentIdentity,
  SyncNewDebugSnapshot,
  WsStatus,
  WsTransportMode,
  WsTransportProbeState,
  YoloFlowStep,
} from "@/hooks/thread-sync-client-types"

export interface ThreadSyncViewState {
  status: WsStatus
  transportMode: WsTransportMode
  hasConnected: boolean
  firstConnectionAttemptStartedAt: number | null
  wsStatusChangedAt: number
  wsDegradedSinceAt: number | null
  wsTransportProbeState: WsTransportProbeState
  wsTransportProbeAt: number | null
  remoteWsDiagnostics: RemoteWsDiagnostics | null
  lastWsCloseDiagnostics: ClientWsCloseDiagnostics | null
  lastWsErrorDiagnostics: ClientWsErrorDiagnostics | null
  initReady: boolean
  agentType: SessionAgentIdentity
  sessionStatus: string
  connectionCount: number
  branchSwitchInProgress: boolean
  startNewWorkGitPending: boolean
  sandboxStatus: SandboxStatus
  sandboxProgress: SandboxProgress[]
  sandboxStartupKind: SandboxStartupQueueKind | null
  /** How this sandbox was booted: "bare" | "prebuilt" | "snapshot" | "resume" | null */
  bootSource: string | null
  queuedMessages: QueuedMessage[]
  ptyUrl: string | null
  vscodeUrl: string | null
  previewUrl: string | null
  previewEndpoints: PreviewEndpoint[]
  runtimeProcesses: RuntimeProcessSnapshot[]
  devServerStatus: DevServerStatus
  devServerError: string | null
  runtimeProcessesConfigured: boolean
  codeServerStatus: CodeServerStatus
  codeServerError: string | null
  activeCredentialSource: ActiveCredentialSourceSnapshot | null
  nextCredentialSource: ActiveCredentialSourceSnapshot | null
  credentialIssue: CredentialIssueSnapshot | null
  credentialStateRefreshedAt: number | null
  lastCredentialFallbackAt: number | null
  runtimeLastErrorCode: string | null
  runtimeLastErrorMessage: string | null
  chatGptOauthDebug: ChatGptOauthDebug | null
  conversationEpoch: number
  activeEditMessageOperation: ActiveEditMessageOperation | null
  lastMessageRejection: MessageRejectionEvent | null
  /** Includes hidden transport work like validated-prefix verification prefill. */
  transportLoading: boolean
  /**
   * Foreground route has activated this thread but the first sync.ready has not
   * confirmed the transcript cursor yet.
   */
  foregroundActivationLoading: boolean
  /** Allows the UI to hide validated-prefix verification once content/empty state is renderable. */
  hideTransportLoadingWhenRenderable: boolean
  isCatchingUp: boolean
  syncReadyReceived: boolean
  runtimeMaterialization: ThreadRuntimeMaterialization
  syncNewDebug: SyncNewDebugSnapshot
  sessionActionNotice: SessionActionOutcomeNotice | null
  queuedSessionCommandNotice: QueuedSessionCommandOutcomeNotice | null
  yoloFlowStep: YoloFlowStep
  yoloFlowError: string | null
  prAdoption: PrAdoptionControlPlaneSnapshot | null
  prAdoptionFreshSession: boolean | null
  sessionModelIntent: StoredSessionIntent | null | undefined
  sessionModelIntentConversationId: string | null
  shouldRefreshSessionSurfaceAfterPrefill: boolean
  transcriptFreshness: ThreadTranscriptFreshness
  localRawCursor: number | null
  targetTerminalRawCursor: number | null
  serverRawTailId: number | null
}

export type ThreadTranscriptFreshness =
  | "unknown"
  | "current"
  | "needs_catchup"
  | "catching_up"

type ActiveRuntimeViewState = {
  agentType: AgentType | null
  snapshot: AgentRuntimeSnapshot | null
  lastEventId: number | null
} | null

export function buildThreadSyncViewState(input: {
  transport: {
    status: WsStatus
    hasConnected: boolean
    firstConnectionAttemptStartedAt: number | null
    wsStatusChangedAt: number
    wsDegradedSinceAt: number | null
    wsTransportProbeState: WsTransportProbeState
    wsTransportProbeAt: number | null
    lastWsCloseDiagnostics: ClientWsCloseDiagnostics | null
    lastWsErrorDiagnostics: ClientWsErrorDiagnostics | null
  }
  controlPlane: {
    initReady: boolean
    agentType: SessionAgentIdentity
    sessionStatus: string
    connectionCount: number
    branchSwitchInProgress: boolean
    startNewWorkGitPending: boolean
    firstDispatchBranchRenameInProgress: boolean
    sandboxStatus: SandboxStatus
    sandboxProgress: SandboxProgress[]
    sandboxStartupKind: SandboxStartupQueueKind | null
    bootSource: string | null
    ptyUrl: string | null
    vscodeUrl: string | null
    previewUrl: string | null
    previewEndpoints: PreviewEndpoint[]
    runtimeProcesses: RuntimeProcessSnapshot[]
    devServerStatus: DevServerStatus
    devServerError: string | null
    runtimeProcessesConfigured: boolean
    activeCredentialSource: ActiveCredentialSourceSnapshot | null
    nextCredentialSource: ActiveCredentialSourceSnapshot | null
    credentialIssue: CredentialIssueSnapshot | null
    credentialStateRefreshedAt: number | null
    lastCredentialFallbackAt: number | null
    runtimeLastErrorCode: string | null
    runtimeLastErrorMessage: string | null
    conversationEpoch: number
    activeEditMessageOperation: ActiveEditMessageOperation | null
    chatGptOauthDebug: ChatGptOauthDebug | null
    yoloFlowStep: YoloFlowStep
    yoloFlowError: string | null
    remoteWsDiagnostics: RemoteWsDiagnostics | null
    prAdoption: PrAdoptionControlPlaneSnapshot | null
    prAdoptionFreshSession: boolean | null
    sessionModelIntent: StoredSessionIntent | null | undefined
    sessionModelIntentConversationId?: string | null
  }
  queue: {
    localOutboxEntries: LocalOutboxQueueRecord[]
    serverPendingMessages: PendingQueueRecord[]
    serverPendingSessionActions: PendingQueueRecord[]
    lastMessageRejection: MessageRejectionEvent | null
  }
  effects: {
    codeServerStatus: CodeServerStatus
    codeServerError: string | null
    sessionActionNotice: SessionActionOutcomeNotice | null
    queuedSessionCommandNotice: QueuedSessionCommandOutcomeNotice | null
  }
  resume: {
    cursor: number
    isCatchingUp: boolean
    syncReadyReceived: boolean
    lastPageCount: number
    lastPageBytes: number
  }
  transportLoading: boolean
  foregroundActivationLoading: boolean
  hideTransportLoadingWhenRenderable: boolean
  runtime: {
    materialization: ThreadRuntimeMaterialization
    renderSource: ThreadSyncRenderSource
    resumeSource: ThreadSyncResumeSource
    lastApplyError: { reason: string } | null
    lastSyncReadyDecision: SyncNewDebugSnapshot["lastSyncReadyDecision"]
    lastReset: SyncNewDebugSnapshot["lastReset"]
  }
  activeRuntimeState: ActiveRuntimeViewState
  activeRuntimeActiveEditOperation?: ActiveEditMessageOperation | null
  /**
   * The thread-level activity state ("idle" | "running" | "error") from the
   * ThreadRuntime store. Used to decide whether "Will send after the current
   * response" is appropriate (only when the agent is actively responding).
   */
  threadActivityState?: "idle" | "running" | "error" | null
  shouldRefreshSessionSurfaceAfterPrefill: boolean
  transcriptFreshness?: ThreadTranscriptFreshness
  localRawCursor?: number | null
  targetTerminalRawCursor?: number | null
  serverRawTailId?: number | null
}): ThreadSyncViewState {
  const { activeRuntimeState, controlPlane, effects, queue, resume, runtime, transport } = input
  const queuedMessages = buildQueuedMessages({
    localOutboxEntries: queue.localOutboxEntries,
    serverPendingMessages: queue.serverPendingMessages,
    serverPendingSessionActions: queue.serverPendingSessionActions,
    presentation: {
      sandboxStatus: controlPlane.sandboxStatus,
      bootSource: controlPlane.bootSource,
      sandboxStartupKind: controlPlane.sandboxStartupKind,
      transportConnected: transport.status === "connected",
      activeEditOperationState: controlPlane.activeEditMessageOperation?.state ?? null,
      firstDispatchBranchRenameInProgress: controlPlane.firstDispatchBranchRenameInProgress,
      transportAuthoritativeReady: controlPlane.initReady && resume.isCatchingUp === false,
      prAdoptionActive:
        controlPlane.prAdoption !== null &&
        (controlPlane.prAdoption.status === "accepted" || controlPlane.prAdoption.status === "running"),
      threadActivityState: input.threadActivityState ?? null,
    },
  })

  return {
    status: transport.status,
    transportMode: "websocket",
    hasConnected: transport.hasConnected,
    firstConnectionAttemptStartedAt: transport.firstConnectionAttemptStartedAt,
    wsStatusChangedAt: transport.wsStatusChangedAt,
    wsDegradedSinceAt: transport.wsDegradedSinceAt,
    wsTransportProbeState: transport.wsTransportProbeState,
    wsTransportProbeAt: transport.wsTransportProbeAt,
    remoteWsDiagnostics: controlPlane.remoteWsDiagnostics,
    lastWsCloseDiagnostics: transport.lastWsCloseDiagnostics,
    lastWsErrorDiagnostics: transport.lastWsErrorDiagnostics,
    initReady: controlPlane.initReady,
    agentType: activeRuntimeState?.agentType ?? controlPlane.agentType,
    sessionStatus: controlPlane.sessionStatus,
    connectionCount: controlPlane.connectionCount,
    branchSwitchInProgress: controlPlane.branchSwitchInProgress,
    startNewWorkGitPending: controlPlane.startNewWorkGitPending,
    sandboxStatus: controlPlane.sandboxStatus,
    sandboxProgress: controlPlane.sandboxProgress,
    sandboxStartupKind: controlPlane.sandboxStartupKind,
    bootSource: controlPlane.bootSource,
    queuedMessages,
    ptyUrl: controlPlane.ptyUrl,
    vscodeUrl: controlPlane.vscodeUrl,
    previewUrl: controlPlane.previewUrl,
    previewEndpoints: controlPlane.previewEndpoints,
    runtimeProcesses: controlPlane.runtimeProcesses,
    devServerStatus: controlPlane.devServerStatus,
    devServerError: controlPlane.devServerError,
    runtimeProcessesConfigured: controlPlane.runtimeProcessesConfigured,
    codeServerStatus: effects.codeServerStatus,
    codeServerError: effects.codeServerError,
    activeCredentialSource: controlPlane.activeCredentialSource,
    nextCredentialSource: controlPlane.nextCredentialSource,
    credentialIssue: controlPlane.credentialIssue,
    credentialStateRefreshedAt: controlPlane.credentialStateRefreshedAt,
    lastCredentialFallbackAt: controlPlane.lastCredentialFallbackAt,
    runtimeLastErrorCode: controlPlane.runtimeLastErrorCode,
    runtimeLastErrorMessage: controlPlane.runtimeLastErrorMessage,
    chatGptOauthDebug: controlPlane.chatGptOauthDebug,
    conversationEpoch: activeRuntimeState?.snapshot?.conversationEpoch ?? controlPlane.conversationEpoch,
    activeEditMessageOperation:
      input.activeRuntimeActiveEditOperation ??
      controlPlane.activeEditMessageOperation,
    lastMessageRejection: queue.lastMessageRejection,
    transportLoading: input.transportLoading,
    foregroundActivationLoading: input.foregroundActivationLoading,
    hideTransportLoadingWhenRenderable: input.hideTransportLoadingWhenRenderable,
    isCatchingUp: resume.isCatchingUp,
    syncReadyReceived: resume.syncReadyReceived,
    runtimeMaterialization: runtime.materialization,
    syncNewDebug: {
      committedCursor: resume.cursor,
      lastAppliedId: activeRuntimeState?.lastEventId ?? resume.cursor,
      lastPageCount: resume.lastPageCount,
      lastPageBytes: resume.lastPageBytes,
      isCatchingUp: resume.isCatchingUp,
      renderSource: runtime.renderSource,
      resumeSource: runtime.resumeSource,
      lastApplyError: runtime.lastApplyError,
      lastSyncReadyDecision: runtime.lastSyncReadyDecision,
      lastReset: runtime.lastReset,
    },
    sessionActionNotice: effects.sessionActionNotice,
    queuedSessionCommandNotice: effects.queuedSessionCommandNotice,
    yoloFlowStep: controlPlane.yoloFlowStep,
    yoloFlowError: controlPlane.yoloFlowError,
    prAdoption: controlPlane.prAdoption,
    prAdoptionFreshSession: controlPlane.prAdoptionFreshSession,
    sessionModelIntent: controlPlane.sessionModelIntent,
    sessionModelIntentConversationId: controlPlane.sessionModelIntentConversationId ?? null,
    shouldRefreshSessionSurfaceAfterPrefill: input.shouldRefreshSessionSurfaceAfterPrefill,
    transcriptFreshness: input.transcriptFreshness ?? "unknown",
    localRawCursor: input.localRawCursor ?? null,
    targetTerminalRawCursor: input.targetTerminalRawCursor ?? null,
    serverRawTailId: input.serverRawTailId ?? null,
  }
}
